import { createStore } from 'vuex'
import axios from 'axios'
import router from '@/router' // Import router for navigation

import requests from './modules/requests'
import talents from './modules/talents'
import candidateCompare from './modules/candidateCompare'
import analyticsModule from './modules/analytics'
import shareCandidates from './modules/shareCandidates';
import companiesModule from './modules/companies';
import activityLogsModule from './modules/activityLogs';
import skillsModule from './modules/skills';

// Create an axios instance for API calls
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add request interceptor for authentication
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
}, error => {
  return Promise.reject(error)
})

// Add response interceptor for error handling
api.interceptors.response.use(response => {
  return response
}, error => {
  // Handle 401 errors by logging out the user
  if (error.response && error.response.status === 401) {
    // Use the global event to avoid circular dependencies
    window.dispatchEvent(new CustomEvent('auth:unauthorized'))
  }
  return Promise.reject(error)
})

// Auth module that integrates with our backend API
const authModule = {
  namespaced: true,
  state: {
    token: localStorage.getItem('token') || null,
    user: JSON.parse(localStorage.getItem('user')) || null,
    loading: false,
    error: null
  },
  getters: {
    isAuthenticated: (state) => !!state.token,
    currentUser: (state) => state.user,
    userRole: (state) => state.user ? state.user.role : null,
    isAdmin: (state) => state.user ? state.user.role === 'admin' : false,
    isSuperAdmin: (state) => state.user ? state.user.role === 'superadmin' : false,
    isAdminOrSuperAdmin: state =>
        ['admin','superadmin'].includes(state.user?.role),
    isClient: (state) => state.user ? state.user.role === 'client' : false,
    isCandidate: (state) => state.user ? state.user.role === 'candidate' : false,
    loading: (state) => state.loading,
    error: (state) => state.error
  },
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
      if (token) {
        localStorage.setItem('token', token)
      } else {
        localStorage.removeItem('token')
      }
    },
    SET_USER(state, user) {
      state.user = user
      if (user) {
        localStorage.setItem('user', JSON.stringify(user))
      } else {
        localStorage.removeItem('user')
      }
    },
    SET_LOADING(state, status) {
      state.loading = status
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    CLEAR_ERROR(state) {
      state.error = null
    }
  },
  actions: {
    // Login user
    async login({ commit, dispatch }, { email, password }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        // Call the backend login API
        const response = await api.post('/auth/login', { email, password })

        // Extract the data
        const { token, user } = response.data

        // Save token and user data
        commit('SET_TOKEN', token)
        commit('SET_USER', user)

        // Fetch complete user data if needed
        if (!user || !user.role) {
          dispatch('fetchUser')
        }

        return response.data
      } catch (error) {
        let errorMessage = 'Login failed'

        if (error.response) {
          errorMessage = error.response.data.message || errorMessage
        } else if (error.request) {
          errorMessage = 'No response from server. Please check your connection.'
        } else {
          errorMessage = error.message
        }

        commit('SET_ERROR', errorMessage)
        throw new Error(errorMessage)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Register a new user
    async register({ commit }, userData) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        const response = await api.post('/auth/register', userData)
        return response.data
      } catch (error) {
        let errorMessage = 'Registration failed'

        if (error.response) {
          errorMessage = error.response.data.message || errorMessage
        } else if (error.request) {
          errorMessage = 'No response from server. Please check your connection.'
        } else {
          errorMessage = error.message
        }

        commit('SET_ERROR', errorMessage)
        throw new Error(errorMessage)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Fetch current user data
    async fetchUser({ commit, state }) {
      if (!state.token) return null

      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        const response = await api.get('/auth/user')
        const { user } = response.data

        commit('SET_USER', user)
        return user
      } catch (error) {
        let errorMessage = 'Failed to load user data'

        if (error.response) {
          if (error.response.status === 401) {
            // Token is invalid or expired
            commit('SET_TOKEN', null)
            commit('SET_USER', null)
          }
          errorMessage = error.response.data.message || errorMessage
        } else if (error.request) {
          errorMessage = 'No response from server. Please check your connection.'
        } else {
          errorMessage = error.message
        }

        commit('SET_ERROR', errorMessage)
        throw new Error(errorMessage)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Request password reset
    async requestPasswordReset({ commit }, { email }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        const response = await api.post('/auth/request-reset', { email })
        return response.data
      } catch (error) {
        let errorMessage = 'Failed to request password reset'

        if (error.response) {
          errorMessage = error.response.data.message || errorMessage
        } else if (error.request) {
          errorMessage = 'No response from server. Please check your connection.'
        } else {
          errorMessage = error.message
        }

        commit('SET_ERROR', errorMessage)
        throw new Error(errorMessage)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Reset password with token
    async resetPassword({ commit }, { token, password }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        const response = await api.post('/auth/reset-password', { token, password })
        return response.data
      } catch (error) {
        let errorMessage = 'Failed to reset password'

        if (error.response) {
          errorMessage = error.response.data.message || errorMessage
        } else if (error.request) {
          errorMessage = 'No response from server. Please check your connection.'
        } else {
          errorMessage = error.message
        }

        commit('SET_ERROR', errorMessage)
        throw new Error(errorMessage)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Log out the user (updated to implement Option 2)
    async logout({ commit }, { redirect } = {}) {
      try {
        // You can add an API call to invalidate the token on the server if implemented
        // await api.post('/auth/logout')

        // Clear token and user from state/localStorage
        commit('SET_TOKEN', null)
        commit('SET_USER', null)

        // Call the redirect callback if provided
        if (typeof redirect === 'function') {
          redirect()
        }
      } catch (error) {
        console.error('Logout error:', error)
      }
    },

    // Check auth state on app initialization
    checkAuthState({ dispatch, state }) {
      // If we have a token, verify it by getting the user data
      if (state.token) {
        return dispatch('fetchUser').catch(() => {
          // If fetching user fails, the token might be invalid
          // No need to do anything as fetchUser already handles token reset
        })
      }
      return Promise.resolve(null)
    }
  }
};

// Import users module
import usersModule from './modules/users';

// Create store with modules
const store = createStore({
  state: {
    appVersion: process.env.VUE_APP_VERSION || '1.0.0',
    appName: process.env.VUE_APP_NAME || 'Recruitment System'
  },
  getters: {
    appVersion: state => state.appVersion,
    appName: state => state.appName
  },
  mutations: {
  },
  actions: {
    // Initialize the application
    init({ dispatch }) {
      // Check the authentication state
      dispatch('auth/checkAuthState')
    },

    // Convenience method for components to logout and redirect
    logoutAndRedirect({ dispatch }) {
      dispatch('auth/logout', {
        redirect: () => router.push({ name: 'login' })
      })
    }
  },
  modules: {
    auth: authModule,
    users: usersModule,
    requests,
    talents,
    candidateCompare,
    analytics: analyticsModule,
    shareCandidates,
    companies: companiesModule,
    activityLogs: activityLogsModule,
    skills: skillsModule
  }
})

// Add event listener for auth unauthorized events
window.addEventListener('auth:unauthorized', () => {
  store.dispatch('logoutAndRedirect')
})

// Initialize store
store.dispatch('init')

export default store
